import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { api, apiError, fileUrl } from "../lib/api";
import { PACKAGES, BANKING } from "../data/site";
import DashboardShell from "../components/DashboardShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Copy, Upload, CheckCircle2, Wallet, Calendar, HandHeart, ListTodo, Download, FileText } from "lucide-react";

const statusBadge = (s) => {
  if (s === "verified" || s === "approved") return <Badge className="bg-emerald-100 text-emerald-700">{s}</Badge>;
  if (s === "rejected") return <Badge className="bg-red-100 text-red-700">{s}</Badge>;
  return <Badge className="bg-amber-100 text-amber-700">{s}</Badge>;
};

export default function MemberDashboard() {
  const { user, refreshUser } = useAuth();
  const [summary, setSummary] = useState(null);
  const [pay, setPay] = useState({ amount: "", method: "eft", note: "" });
  const [proofFile, setProofFile] = useState(null);
  const [assist, setAssist] = useState({ amount: "", purpose: "", description: "" });
  const [reminder, setReminder] = useState("");
  const [msg, setMsg] = useState({ subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [myReqs, setMyReqs] = useState([]);
  const [documents, setDocuments] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState("");

  const load = async () => {
    try {
      const [s, t, r, d] = await Promise.all([api.get("/me/summary"), api.get("/tasks"), api.get("/assistance"), api.get("/me/documents")]);
      setSummary(s.data); setReminder(s.data.reminder_day || ""); setTasks(t.data); setMyReqs(r.data); setDocuments(d.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const joinTask = async (id) => {
    try { await api.post(`/tasks/${id}/participate`, {}); toast.success("You've joined this task!"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const downloadLetter = async (id) => {
    try {
      const res = await api.get(`/assistance/${id}/letter`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `GDF-Assistance-${id.slice(0, 8)}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const copyRef = () => { navigator.clipboard.writeText(summary?.customer_reference || ""); toast.success("Reference copied!"); };

  const submitPayment = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      let proofId = null;
      if (proofFile) { const fd = new FormData(); fd.append("purpose", "proof_of_payment"); fd.append("file", proofFile); const { data } = await api.post("/uploads", fd); proofId = data.id; }
      await api.post("/payments", { amount: Number(pay.amount), method: pay.method, proof_file_id: proofId, note: pay.note || null });
      toast.success("Payment recorded! Awaiting verification.");
      setPay({ amount: "", method: "eft", note: "" }); setProofFile(null); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const submitAssist = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/assistance", { amount: Number(assist.amount), purpose: assist.purpose, description: assist.description || null }); toast.success("Request submitted!"); setAssist({ amount: "", purpose: "", description: "" }); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const saveReminder = async () => {
    try { await api.post("/me/reminder", { reminder_day: Number(reminder) }); toast.success("Reminder day saved!"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const sendMsg = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/messages", msg); toast.success("Message sent to your agent!"); setMsg({ subject: "", body: "" }); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const uploadDoc = async (purpose, file) => {
    if (!file) return;
    setUploadingDoc(purpose);
    try {
      const fd = new FormData(); fd.append("purpose", purpose); fd.append("file", file);
      await api.post("/uploads", fd);
      toast.success("Document uploaded!");
      const { data } = await api.get("/me/documents"); setDocuments(data);
      if (purpose === "profile_picture") refreshUser();
    } catch (err) { toast.error(apiError(err)); } finally { setUploadingDoc(""); }
  };

  const DocCard = ({ title, purpose, accept, doc, hint }) => (
    <div className="border border-slate-200 rounded-xl p-4" data-testid={`doc-card-${purpose}`}>
      <div className="flex items-center justify-between">
        <p className="font-medium text-[#0A192F] text-sm">{title}</p>
        {doc ? <Badge className="bg-emerald-100 text-emerald-700">Uploaded</Badge> : <Badge className="bg-slate-100 text-slate-500">Not uploaded</Badge>}
      </div>
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
      {doc && <a href={fileUrl(doc.storage_path)} target="_blank" rel="noreferrer" data-testid={`doc-view-${purpose}`} className="text-emerald-600 text-sm inline-flex items-center gap-1 mt-2"><FileText className="w-4 h-4" /> View current file</a>}
      <label className="mt-3 flex items-center gap-2 border-2 border-dashed border-slate-300 hover:border-emerald-400 rounded-lg px-3 py-2 cursor-pointer text-sm text-slate-600 transition-colors">
        {uploadingDoc === purpose ? <CheckCircle2 className="w-4 h-4 text-emerald-600 animate-pulse" /> : <Upload className="w-4 h-4 text-slate-400" />}
        {uploadingDoc === purpose ? "Uploading..." : (doc ? "Replace file" : "Upload file")}
        <input type="file" accept={accept} className="hidden" data-testid={`upload-${purpose}`} onChange={(e) => uploadDoc(purpose, e.target.files[0])} />
      </label>
    </div>
  );

  const pkg = PACKAGES.find((p) => p.id === summary?.package);

  return (
    <DashboardShell title={`Hi, ${user?.name?.split(" ")[0]} 👋`} subtitle="Your Givers Driven Funds membership">
      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="stat-package">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Package</p>
          <p className="text-xl font-head font-bold text-[#0A192F] mt-1">{pkg?.name || "Not set"}</p>
          <p className="text-sm text-emerald-600 mt-1">R{summary?.monthly_amount || 0}/month</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="stat-contributed">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Total contributed</p>
          <p className="text-xl font-head font-bold text-[#0A192F] mt-1">R{summary?.total_contributed?.toLocaleString() || 0}</p>
          <p className="text-sm text-slate-400 mt-1">verified payments</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="stat-progress">
          <p className="text-xs text-slate-500 uppercase tracking-wide">6-month progress</p>
          <p className="text-xl font-head font-bold text-[#0A192F] mt-1">{summary?.months_paid || 0} / 6</p>
          <Progress value={Math.min(100, ((summary?.months_paid || 0) / 6) * 100)} className="mt-2 h-2" />
        </div>
        <div className={`rounded-2xl border p-5 ${summary?.eligible ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-slate-200"}`} data-testid="stat-eligible">
          <p className={`text-xs uppercase tracking-wide ${summary?.eligible ? "text-emerald-100" : "text-slate-500"}`}>Assistance</p>
          <p className="text-xl font-head font-bold mt-1">{summary?.eligible ? "Eligible ✓" : `In ${summary?.months_remaining || 6} months`}</p>
          <p className={`text-sm mt-1 ${summary?.eligible ? "text-emerald-100" : "text-slate-400"}`}>Est. up to R{summary?.estimated_help?.toLocaleString() || 0}</p>
        </div>
      </div>

      {/* Reference banner */}
      <div className="bg-[#0A192F] text-white rounded-2xl p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="reference-banner">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wide">Your unique payment reference</p>
          <p className="text-2xl font-mono-gdf font-bold text-amber-400 mt-1" data-testid="customer-reference">{summary?.customer_reference || "—"}</p>
          <p className="text-xs text-slate-400 mt-1">Always use this as your bank reference.</p>
        </div>
        <Button onClick={copyRef} data-testid="copy-reference-btn" className="bg-white/10 hover:bg-white/20 text-white"><Copy className="w-4 h-4 mr-1" /> Copy</Button>
      </div>

      <Tabs defaultValue="pay">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="pay" data-testid="tab-pay"><Wallet className="w-4 h-4 mr-1" /> Make a payment</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Payment history</TabsTrigger>
          <TabsTrigger value="assist" data-testid="tab-assist"><HandHeart className="w-4 h-4 mr-1" /> Request help</TabsTrigger>
          <TabsTrigger value="reminder" data-testid="tab-reminder"><Calendar className="w-4 h-4 mr-1" /> Reminder</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks"><ListTodo className="w-4 h-4 mr-1" /> Tasks</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><FileText className="w-4 h-4 mr-1" /> Documents</TabsTrigger>
          <TabsTrigger value="message" data-testid="tab-message">Message agent</TabsTrigger>
        </TabsList>

        <TabsContent value="pay">
          <div className="grid lg:grid-cols-2 gap-6 mt-4">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-head font-semibold text-[#0A192F] mb-4">Record a payment</h3>
              <form onSubmit={submitPayment} className="space-y-4">
                <div><Label>Amount (R)</Label><Input type="number" min={10} required value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="pay-amount" className="mt-1" /></div>
                <div>
                  <Label>Payment method</Label>
                  <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
                    <SelectTrigger className="mt-1" data-testid="pay-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="eft">EFT (bank transfer)</SelectItem>
                      <SelectItem value="deposit">Cash deposit at branch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Note (optional)</Label><Input value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} data-testid="pay-note" className="mt-1" /></div>
                <div>
                  <Label>Proof of payment</Label>
                  <label className={`mt-1 flex items-center gap-3 border-2 border-dashed rounded-xl px-4 py-3 cursor-pointer ${proofFile ? "border-emerald-400 bg-emerald-50" : "border-slate-300"}`}>
                    {proofFile ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Upload className="w-5 h-5 text-slate-400" />}
                    <span className="text-sm text-slate-600 truncate">{proofFile ? proofFile.name : "Upload receipt (image or PDF)"}</span>
                    <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="pay-proof" onChange={(e) => setProofFile(e.target.files[0])} />
                  </label>
                </div>
                <Button type="submit" disabled={busy} data-testid="pay-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full w-full">{busy ? "Saving..." : "Record payment"}</Button>
              </form>
            </div>
            <div className="bg-[#0A192F] text-white rounded-2xl p-6">
              <h3 className="font-head font-semibold mb-4">Pay into our FNB account</h3>
              <div className="space-y-3 text-sm">
                {[["Account name", BANKING.account_name], ["Bank", BANKING.bank_name], ["Account type", BANKING.account_type], ["Account number", BANKING.account_number], ["Branch code", BANKING.branch_code], ["Reference", summary?.customer_reference || BANKING.reference_format]].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-white/10 pb-2"><span className="text-slate-400">{k}</span><span className="font-mono-gdf font-semibold">{v}</span></div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="history">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-4">
            <h3 className="font-head font-semibold text-[#0A192F] mb-4">Your payments</h3>
            {summary?.payments?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="payments-table">
                  <thead><tr className="text-left text-slate-500 border-b"><th className="py-2">Date</th><th>Amount</th><th>Method</th><th>Status</th></tr></thead>
                  <tbody>
                    {summary.payments.map((p) => (
                      <tr key={p.id} className="border-b border-slate-100">
                        <td className="py-2">{new Date(p.created_at).toLocaleDateString()}</td>
                        <td className="font-semibold">R{p.amount}</td>
                        <td className="capitalize">{p.method}</td>
                        <td>{statusBadge(p.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-slate-400 text-sm">No payments yet. Make your first contribution above.</p>}
          </div>
        </TabsContent>

        <TabsContent value="assist">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-4 max-w-xl">
            <h3 className="font-head font-semibold text-[#0A192F] mb-1">Request assistance</h3>
            <p className="text-sm text-slate-500 mb-4">{summary?.eligible ? "You're eligible — tell us how we can help." : `You can request now; assistance opens after 6 months of contributions (${summary?.months_remaining} to go).`}</p>
            <form onSubmit={submitAssist} className="space-y-4">
              <div><Label>Amount needed (R)</Label><Input type="number" required value={assist.amount} onChange={(e) => setAssist({ ...assist, amount: e.target.value })} data-testid="assist-amount" className="mt-1" /></div>
              <div><Label>Purpose</Label><Input required value={assist.purpose} onChange={(e) => setAssist({ ...assist, purpose: e.target.value })} data-testid="assist-purpose" className="mt-1" placeholder="e.g. School fees, medical bill" /></div>
              <div><Label>Details</Label><Textarea rows={3} value={assist.description} onChange={(e) => setAssist({ ...assist, description: e.target.value })} data-testid="assist-description" className="mt-1" /></div>
              <Button type="submit" disabled={busy} data-testid="assist-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full">Submit request</Button>
            </form>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-4 max-w-xl">
            <h3 className="font-head font-semibold text-[#0A192F] mb-3">My requests</h3>
            {myReqs.length ? (
              <div className="space-y-3" data-testid="my-requests-list">
                {myReqs.map((r) => (
                  <div key={r.id} className="border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2"><p className="font-semibold text-[#0A192F]">R{r.amount}</p>{statusBadge(r.status)}</div>
                      <p className="text-sm text-slate-500">{r.purpose}</p>
                    </div>
                    {r.status === "approved" && (
                      <Button size="sm" onClick={() => downloadLetter(r.id)} data-testid={`download-letter-${r.id}`} className="bg-[#0A192F] text-white rounded-full"><Download className="w-4 h-4 mr-1" /> Letter</Button>
                    )}
                  </div>
                ))}
              </div>
            ) : <p className="text-slate-400 text-sm">No requests yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="tasks">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-4">
            <h3 className="font-head font-semibold text-[#0A192F] mb-1">Community tasks</h3>
            <p className="text-sm text-slate-500 mb-4">Tasks are optional, but joining in helps the community grow. 💚</p>
            {tasks.length ? (
              <div className="grid gap-4 sm:grid-cols-2" data-testid="tasks-list">
                {tasks.map((t) => (
                  <div key={t.id} className="border border-slate-200 rounded-xl p-4" data-testid={`task-card-${t.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-head font-semibold text-[#0A192F]">{t.title}</p>
                      {t.reward && <span className="text-[11px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold whitespace-nowrap">{t.reward}</span>}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{t.description}</p>
                    <Button size="sm" disabled={t.joined} onClick={() => joinTask(t.id)} data-testid={`join-task-${t.id}`} className={`mt-3 rounded-full ${t.joined ? "bg-slate-200 text-slate-500" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}>
                      {t.joined ? "Joined ✓" : "Join task"}
                    </Button>
                  </div>
                ))}
              </div>
            ) : <p className="text-slate-400 text-sm">No tasks posted right now. Check back soon!</p>}
          </div>
        </TabsContent>

        <TabsContent value="reminder">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-4 max-w-md">
            <h3 className="font-head font-semibold text-[#0A192F] mb-1">Monthly payment reminder</h3>
            <p className="text-sm text-slate-500 mb-4">Pick the day of the month you'd like to be reminded to pay.</p>
            <div className="flex gap-3 items-end">
              <div className="flex-1"><Label>Day of month (1–28)</Label><Input type="number" min={1} max={28} value={reminder} onChange={(e) => setReminder(e.target.value)} data-testid="reminder-day" className="mt-1" /></div>
              <Button onClick={saveReminder} data-testid="reminder-save" className="bg-[#0A192F] text-white rounded-full">Save</Button>
            </div>
            {summary?.reminder_day && <p className="text-sm text-emerald-600 mt-3">Reminder set for day {summary.reminder_day} each month.</p>}
          </div>
        </TabsContent>

        <TabsContent value="documents">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-4">
            <h3 className="font-head font-semibold text-[#0A192F]">My documents</h3>
            <p className="text-sm text-slate-500 mt-1">All optional — upload these whenever you're ready, at your own pace. They help us verify your identity and assist you faster.</p>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <DocCard title="Certified ID document" purpose="id_document" accept="image/*,application/pdf" doc={documents?.id_document} hint="A certified copy of your ID (image or PDF)." />
              <DocCard title="Profile picture" purpose="profile_picture" accept="image/*" doc={documents?.profile_picture} hint="A clear photo of yourself." />
            </div>

            <div className="mt-6 border-t border-slate-100 pt-5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h4 className="font-head font-semibold text-[#0A192F]">Bank statements</h4>
                  <p className="text-xs text-slate-400">Upload one or more statements (image or PDF). You can add more anytime.</p>
                </div>
                <label className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-4 py-2 text-sm cursor-pointer transition-colors">
                  <Upload className="w-4 h-4" /> {uploadingDoc === "bank_statement" ? "Uploading..." : "Upload statement"}
                  <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="upload-bank_statement" onChange={(e) => uploadDoc("bank_statement", e.target.files[0])} />
                </label>
              </div>
              {documents?.bank_statements?.length ? (
                <div className="mt-3 space-y-2" data-testid="bank-statements-list">
                  {documents.bank_statements.map((f) => (
                    <div key={f.id} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2">
                      <span className="text-sm text-slate-600 truncate flex items-center gap-2"><FileText className="w-4 h-4 text-emerald-600 shrink-0" /> {f.original_filename}</span>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-xs text-slate-400">{new Date(f.created_at).toLocaleDateString()}</span>
                        <a href={fileUrl(f.storage_path)} target="_blank" rel="noreferrer" className="text-emerald-600 text-sm">View</a>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-slate-400 text-sm mt-3">No bank statements uploaded yet.</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="message">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-4 max-w-xl">
            <h3 className="font-head font-semibold text-[#0A192F] mb-1">Message your agent</h3>
            <p className="text-sm text-slate-500 mb-4">Send a note to the agent who registered you (or the GDF team).</p>
            <form onSubmit={sendMsg} className="space-y-4">
              <div><Label>Subject</Label><Input required value={msg.subject} onChange={(e) => setMsg({ ...msg, subject: e.target.value })} data-testid="msg-subject" className="mt-1" /></div>
              <div><Label>Message</Label><Textarea rows={4} required value={msg.body} onChange={(e) => setMsg({ ...msg, body: e.target.value })} data-testid="msg-body" className="mt-1" /></div>
              <Button type="submit" disabled={busy} data-testid="msg-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full">Send message</Button>
            </form>
          </div>
        </TabsContent>
      </Tabs>
    </DashboardShell>
  );
}
